import type { Experiment } from 'types';
import type { ServerSettings } from '@/state/experiments/useServerSettings';
import { useState } from 'react';
import { toast } from 'sonner';
import { FLAG_KEY_PATTERN } from 'definitions';
import { can } from 'definitions/capabilities';
import { canUseServerSide } from 'definitions/server-side-access';
import { config } from 'config';
import { useAuth } from 'hooks/useAuth';
import { isSavedButUnpublished, UNPUBLISHED_MESSAGE, useServerSettings } from '@/state/experiments/useServerSettings';
import { useVariants } from '@/state/experiments/useVariants';
import { useServerKeys } from '@/state/useServerKeys';
import { isPreStart } from 'helpers/experiment-status';
import { useRuleDraft } from './use-rule-draft';
import { browserSnippet, curlSnippet, sdkSnippet, seedSnippet } from './snippets';
import { setupSteps } from './setup-steps';
import { COPY } from './copy';

type Decider = NonNullable<Experiment['decidedBy']>;
type BucketOn = NonNullable<Experiment['bucketOn']>;

/**
 * Everything the Server-side experiment's Assignment card holds and derives: the mode and why it
 * may be locked, the flag key draft, the attribute rule dialog, and the snippets built from
 * this experiment's real keys. The card and its pieces only render what this returns.
 */
export const useServerSideConfig = (experiment: Experiment) => {
  const { save: saveSettings, isSaving } = useServerSettings(experiment.id);
  const { variants = [] } = useVariants(experiment.id);
  const [flagKeyDraft, setFlagKeyDraft] = useState(experiment.flagKey ?? '');
  const [copiedText, setCopiedText] = useState<string | null>(null);

  const decidedBy: Decider = experiment.decidedBy ?? 'browser';
  const isServer = decidedBy === 'server';
  const isDraft = isPreStart(experiment.status);
  const visitorIdEnabled = !!experiment.website?.visitorIdEnabled;
  const rules = experiment.attributeTargeting ?? [];

  // Draft first: outside Draft nothing about the mode changes, whatever the visitor ID says.
  // Switching back to the browser never needs the visitor ID, so that reason only blocks 'server'.
  const assignmentLockedReason = !isDraft
    ? COPY.assignment.notDraft
    : !isServer && !visitorIdEnabled
      ? COPY.assignment.visitorIdOff
      : null;

  const trimmedFlagKey = flagKeyDraft.trim();
  const flagKeyError = trimmedFlagKey && !FLAG_KEY_PATTERN.test(trimmedFlagKey) ? COPY.flagKey.invalid : null;
  const canSaveFlagKey =
    isDraft && !isSaving && !!trimmedFlagKey && !flagKeyError && trimmedFlagKey !== (experiment.flagKey ?? '');

  const missingVariantKeys = variants.some((variant) => !variant.key);

  const snippetParams = { experiment, variants, trackingUrl: config.TRACKING_URL };
  const snippets = {
    sdk: sdkSnippet(snippetParams),
    seed: seedSnippet(snippetParams),
    curl: curlSnippet(snippetParams),
    browser: browserSnippet(snippetParams),
  };

  // The key list needs server-keys:manage; anyone else sees the step without a tick. Fetched
  // only while the card is on server, the one place the checklist shows.
  const { user } = useAuth();
  const canReadKeys = can(user, 'server-keys:manage') && canUseServerSide(user);
  const { activeKeys, isLoading: keysLoading } = useServerKeys(
    experiment.websiteId ?? 0,
    isServer && canReadKeys && !!experiment.websiteId,
  );
  const steps = setupSteps({
    visitorIdEnabled,
    activeKeys: !canReadKeys || keysLoading ? null : activeKeys.length,
    flagKey: experiment.flagKey,
    variants,
  });
  const conversionsNote =
    experiment.website?.trackingMethod === 'server' ? COPY.setup.conversionsServer : COPY.setup.conversions;

  /** Saves, and says so when a live experiment's change was saved but not published. */
  const save = async (settings: ServerSettings) => {
    const result = await saveSettings(settings);
    if (result && isSavedButUnpublished({ status: experiment.status, result })) toast.warning(UNPUBLISHED_MESSAGE);
    return result;
  };

  const setDecidedBy = async (next: Decider) => {
    if (next === decidedBy || assignmentLockedReason) return;
    // The API requires a bucket for a server-decided experiment; the visitor ID is the default.
    await save(
      next === 'server' ? { decidedBy: next, bucketOn: experiment.bucketOn ?? 'visitor' } : { decidedBy: next },
    );
  };

  const saveFlagKey = async () => {
    if (!canSaveFlagKey) return;
    await save({ flagKey: trimmedFlagKey });
  };

  const setBucketOn = async (next: BucketOn) => {
    if (!isDraft || next === experiment.bucketOn) return;
    await save({ bucketOn: next });
  };

  const ruleEditor = useRuleDraft({
    rules,
    onAdd: async (rule) => !!(await save({ attributeTargeting: [...rules, rule] })),
  });

  const removeRule = async (index: number) => {
    await save({ attributeTargeting: rules.filter((_, idx) => idx !== index) });
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      toast.error(COPY.snippets.copyFailed);
      return;
    }
    setCopiedText(text);
    setTimeout(() => setCopiedText(null), 3000);
  };

  return {
    decidedBy,
    isServer,
    isDraft,
    isSaving,
    assignmentLockedReason,
    setDecidedBy,
    flagKeyDraft,
    setFlagKeyDraft,
    flagKeyError,
    canSaveFlagKey,
    saveFlagKey,
    bucketOn: experiment.bucketOn ?? 'visitor',
    setBucketOn,
    rules,
    ...ruleEditor,
    removeRule,
    missingVariantKeys,
    setupSteps: steps,
    conversionsNote,
    snippets,
    copiedText,
    copy,
  };
};

export type ServerSideConfigManager = ReturnType<typeof useServerSideConfig>;
